import type { Me, Role } from "@/api/types";

/** Roles that may see other people's data in their organisation. */
export const STAFF: readonly Role[] = ["tenant_admin", "manager"];
/** Roles that change the organisation's settings (Statuses, Policies, Organisation): admins only (2026-10-29). */
export const ADMIN: readonly Role[] = ["tenant_admin"];
/** Roles that exist inside an organisation. */
export const TENANT: readonly Role[] = ["tenant_admin", "manager", "employee"];

/**
 * "Access" is what a person may do in Zotula (the API's `role`). It is a fixed list.
 * "Role" in the console means the organisation's own job roles, which admins create.
 */
export const ROLE_LABEL: Record<Role, string> = {
  super_admin: "Platform admin",
  tenant_admin: "Admin",
  manager: "Manager",
  employee: "Employee",
};

/** What the signed-in person sees of others, from `GET /v1/me` (Who sees whom). */
export type Access = Me["access"];

/**
 * Pages that show other people's data are open to admins and managers and also to anyone whose job role sees
 * others: "others" for the people pages, "screenshots" for the screenshots of the people they see, "devices" for the
 * Devices page, which managers never get.
 */
export type Reach = "others" | "screenshots" | "devices";

/** Whether the person sees anyone but themselves. */
export function seesOthers(access: Access | undefined): boolean {
  return access !== undefined && access.sees !== "self";
}

/**
 * Whether a page of this reach is open to someone with this role and access. Never to a platform admin: they belong
 * to no organisation and run the platform from Organisations.
 */
export function reaches(reach: Reach | undefined, access: Access | undefined, role?: Role): boolean {
  if (role === "super_admin") return false;
  // Devices are for admins, and employees whose job role sees others; never managers (2026-10-29).
  if (reach === "devices" && role === "manager") return false;
  if (!reach || !access || !seesOthers(access)) return false;
  return reach === "others" || reach === "devices" || access.see_screenshots;
}

/** Where each role lands after signing in. */
export function homePath(role: Role): string {
  if (role === "super_admin") return "/organisations";
  return role === "employee" ? "/my-day" : "/overview";
}
